import { Box, Button, Flex, NumberInput, NumberInputField, Text, useDisclosure } from '@chakra-ui/react';
import { useWeb3React } from '@web3-react/core';
import BigNumber from 'bignumber.js';
import { isEqual } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWalletModal } from '../../../components/WalletModal';
import useAuth from '../../../hooks/useAuth';
import { formatBigNumber } from '../../../hooks/useModalInfo';
import useWeb3 from '../../../hooks/useWeb3';
import { useBalance } from '../../../redux/bondDetail/hooks';
import { useTransactionAdder } from '../../../redux/transactions/hooks';
import { Bond } from '../../../redux/types';
import { callEstGas } from '../../../utils/callHelpers';
import { formatBondBalance, getBondAbiByType } from '../../../utils/common';
import { useResponseModal } from '../../../utils/confirmModal';
import { getBep20Contract } from '../../../utils/contractHelpers';
import { getBalanceNumber } from '../../../utils/formatBalance';
import multicall from '../../../utils/multicall';
import ConfirmCommit from '../Modal/ConfirmCommitModal';
import { Max_256 } from './PurchaseBondInput';

interface ICommitPhase {
  bondDetail: Bond;
}

const CommitPhase: React.FC<ICommitPhase> = ({ bondDetail }) => {
  const { t } = useTranslation('bonds');
  const { faceAsset, commitInfo = {} } = bondDetail;
  const { account } = useWeb3React();
  const faceAssetBalance = useBalance(faceAsset?.address, account);

  return (
    <>
      <Flex direction={'column'}>
        <Text fontWeight={500} as={'span'} fontSize={'14px'} color={'gray.500'}>
          {t('balance')}:&nbsp;&nbsp;
          <Text as={'span'} fontSize={['14px']} color={'white'}>
            {account ? `${formatBondBalance(faceAssetBalance)} ${faceAsset?.name}` : '--'}
          </Text>
        </Text>
        {!(account && commitInfo.amountCommitted) && (
          <Text fontWeight={500} as={'span'} fontSize={'14px'} color={'gray.500'}>
            {t('amount_committed')}:&nbsp;&nbsp;
            <Text as={'span'} fontSize={['14px']} color={'white'}>
              {account &&
                commitInfo.amountCommitted &&
                `${formatBondBalance(Number(commitInfo.amountCommitted))} ${faceAsset?.name}`}
            </Text>
          </Text>
        )}
        <Text
          hidden={!(account && commitInfo.distributionAmount)}
          fontWeight={500}
          as={'span'}
          fontSize={'14px'}
          color={'gray.500'}
        >
          {t('est_bond_amount')}:&nbsp;&nbsp;
          <Text as={'span'} fontSize={['14px']} color={'white'}>
            {account && commitInfo.distributionAmount && `${formatBondBalance(Number(commitInfo.distributionAmount))}`}{' '}
            units
          </Text>
        </Text>
      </Flex>
      <CommitWithContract bondDetail={bondDetail} account={account} faceAssetBalance={faceAssetBalance} />
    </>
  );
};

interface ICommitWithContract {
  bondDetail: Bond;
  account: string;
  faceAssetBalance: number | string;
}

const CommitWithContract: React.FC<ICommitWithContract> = ({ bondDetail, account, faceAssetBalance }) => {
  const { t } = useTranslation('bonds');
  const { login, logout } = useAuth();
  const { onPresentConnectModal } = useWalletModal(login, logout, account);
  const web3 = useWeb3();
  const [isAllowance, setIsAllowance] = useState(false);
  const [amount, setAmount] = useState('');
  const [maxEstFromLP, setMaxEstFromLP] = useState(0);
  const [amountCommitted, setAmountCommitted] = useState(0);
  const { assetId, faceAsset, bondAddress, lastPrice } = bondDetail;
  const bondAbi = getBondAbiByType(bondDetail?.type || 'normal');
  const availToPurchase =
    Number(maxEstFromLP) - Number(amountCommitted) >= Number(faceAssetBalance)
      ? Number(faceAssetBalance)
      : Number(maxEstFromLP) - Number(amountCommitted);
  // TODO get from server
  const getMaxAmount = () => {
    const balanceString = availToPurchase.toString();
    let valueMax = balanceString;
    if (balanceString.includes('.')) {
      const balanceArray = balanceString.split('.');
      if (balanceArray[1]) {
        if (balanceArray[1].length > 8) {
          const newDecimal = balanceArray[1].slice(0, 8);
          valueMax = `${balanceArray[0]}.${newDecimal}`;
        }
      }
    }
    return Number(valueMax);
  };
  const isMax = Number(amount) > 0 && isEqual(getMaxAmount(), Number(amount));
  const invalidAmount = Number(amount) > getMaxAmount();
  const isDisabledInput = !account;
  const isDisabledButton = useMemo(
    () => isAllowance && (!!invalidAmount || !amount || isNaN(Number(amount)) || Number(amount) <= 0),
    [amount, invalidAmount, isAllowance],
  );
  const { isOpen, onOpen: openConfirmPurchase, onClose } = useDisclosure();

  const getButtonText = () => {
    if (!account) return t('connect');
    if (isAllowance) return t('commit');
    return t('approve');
  };

  const setMaxAmount = () => {
    return setAmount(getMaxAmount().toString());
  };

  const addTransaction = useTransactionAdder();

  const onOpen = () => {
    openConfirmPurchase();
  };

  const onCloseModal = () => {
    getAmountCommit(account);
    onClose();
  };

  const onApprove = useCallback(async () => {
    if (account) {
      try {
        const contract = getBep20Contract(faceAsset?.address, web3);
        const method = contract.methods.approve(bondAddress, Max_256);
        const gas = await callEstGas(method, account);
        method.send({ from: account, gas }, async function (err, hash) {
          if (hash) {
            addTransaction({ hash } as any, {
              trigger: () => {
                getAllowance();
              },
            });
            await useResponseModal({
              title: t('transaction_submitted'),
              txHash: hash,
            });
          }
          if (err) {
            if (err?.message) {
              if (err?.message) {
                await useResponseModal({
                  title: t('approve_failed'),
                  errorMsg: err?.message,
                });
              }
            }
            throw err;
          }
        });
      } catch (e) {
        await useResponseModal({
          title: t('approve_failed'),
          errorMsg: e?.message,
        });
      }
    }
  }, [account, web3]);

  const handleClickButton = () => {
    if (!account) {
      onPresentConnectModal();
    } else {
      if (isAllowance) {
        onOpen();
      } else {
        onApprove();
      }
    }
  };

  const getAllowance = async () => {
    try {
      const [[res], commitLpWei] = await multicall(bondAbi, [
        {
          address: faceAsset?.address,
          name: 'allowance',
          params: [account, bondAddress],
        },
        {
          address: bondAddress,
          name: 'convertAmountStakeCanCommit',
          params: [account],
        },
      ]);
      setIsAllowance(!!getBalanceNumber(Number(res)));
      setMaxEstFromLP(formatBigNumber(new BigNumber(commitLpWei[0]._hex || 0)));
      getAmountCommit(account);
    } catch (e) {}
  };

  const getAmountCommit = async (accountX: string) => {
    try {
      const [[res]] = await multicall(bondAbi, [
        {
          address: bondAddress,
          name: 'amountCommitted',
          params: [accountX],
        },
      ]);
      setAmountCommitted(getBalanceNumber(Number(res[0]._hex || 0)));
    } catch (e) {}
  };

  useEffect(() => {
    getAllowance();
  }, [account]);

  return (
    <Box
      boxShadow={'0px 0px 36px rgba(0, 0, 0, 0.28)'}
      px={['12px', '16px', '20px']}
      py={['12px']}
      borderRadius={'12px'}
      bg={'#1F2835'}
      mt={['16px']}
    >
      <Flex>
        <Text fontWeight={500} as={'span'} fontSize={'14px'} color={'gray.500'}>
          <Text display={['none', 'none', 'inline-block']} as={'span'}>
            {t('avail_to_commit')}:&nbsp;&nbsp;
          </Text>
          <Text display={['inline-block', 'inline-block', 'none']} as={'span'}>
            {t('avail')}:&nbsp;&nbsp;
          </Text>
          <Text as={'span'} fontSize={['14px']} color={'white'}>
            {formatBondBalance(
              Number(maxEstFromLP) - Number(amountCommitted) < 0 ? 0 : Number(maxEstFromLP) - Number(amountCommitted),
            )}{' '}
            {faceAsset?.name}
          </Text>
        </Text>
      </Flex>
      <Flex alignItems={'center'} my={['8px']}>
        <Flex
          flex={1}
          border={'1.5px solid'}
          borderColor={invalidAmount ? 'red.400' : isDisabledInput ? 'gray.700' : 'gray.600'}
          borderRadius={'8px'}
          alignItems={'center'}
        >
          <NumberInput
            id={`input-number-container-staking`}
            isDisabled={isDisabledInput}
            onChange={(valueString) => setAmount(valueString)}
            value={amount}
            variant={'unstyled'}
            alignItems={'center'}
            maxW={'100%'}
            flex={1}
          >
            <NumberInputField
              borderWidth={0}
              color={'white'}
              maxLength={18}
              pr={'0px'}
              pl={'12px'}
              className={'input-number'}
              placeholder={account ? t('enter_amount') : t('wallet_required')}
              fontSize={['14px', '14px', '16px']}
              fontWeight={600}
              _placeholder={{
                color: 'gray.500',
              }}
            />
          </NumberInput>
          <Button
            m={'4px'}
            borderRadius={'6px'}
            bg={isMax ? 'teal.150' : isDisabledInput ? 'gray.700' : 'transparent'}
            color={isMax ? 'white' : isDisabledInput ? 'gray.500' : 'teal.150'}
            border={isDisabledInput ? '' : '1px solid'}
            borderColor={'teal.150'}
            height={'32px'}
            _hover={{
              bg: isDisabledInput ? '' : '',
            }}
            _active={{
              bg: isDisabledInput ? 'gray.300' : 'teal.150',
            }}
            _disabled={{ opacity: isDisabledInput ? '1' : '0.1' }}
            cursor={isDisabledInput ? 'no-drop' : 'pointer'}
            isDisabled={isDisabledInput}
            fontWeight={500}
            fontSize={'14px'}
            onClick={setMaxAmount}
          >
            {t('max')}
          </Button>
        </Flex>
        <Button
          h={'40px'}
          ml={['12px']}
          onClick={handleClickButton}
          color={isDisabledButton ? 'gray.500' : 'white'}
          fontWeight={500}
          fontSize={'14px'}
          borderRadius={'8px'}
          display={['none', 'none', 'block']}
          cursor={isDisabledButton ? 'no-drop' : 'pointer'}
          background={isDisabledButton ? 'gray.700' : 'teal.150'}
          _disabled={{ opacity: isDisabledButton ? '1' : '0.1' }}
          disabled={isDisabledButton}
          _hover={{
            background: isDisabledButton ? '' : 'green.500',
          }}
          _active={{
            background: isDisabledButton ? 'gray.300' : 'teal.150',
          }}
        >
          {getButtonText()}
        </Button>
      </Flex>
      {invalidAmount && (
        <Text fontWeight={400} fontSize={'14px'} color={'red.400'}>
          {t('input_error')}
        </Text>
      )}
      <Button
        h={'40px'}
        mt={['12px']}
        fontWeight={500}
        fontSize={'14px'}
        onClick={handleClickButton}
        width={'100%'}
        color={isDisabledButton ? 'gray.500' : 'white'}
        borderRadius={'8px'}
        display={['block', 'block', 'none']}
        cursor={isDisabledButton ? 'no-drop' : 'pointer'}
        background={isDisabledButton ? 'gray.700' : 'teal.150'}
        _disabled={{ opacity: isDisabledButton ? '1' : '0.1' }}
        disabled={isDisabledButton}
        _hover={{
          background: isDisabledButton ? '' : 'green.500',
        }}
        _active={{
          background: isDisabledButton ? 'gray.300' : 'teal.150',
        }}
      >
        {getButtonText()}
      </Button>
      <ConfirmCommit
        isOpen={isOpen}
        onClose={onCloseModal}
        account={account}
        bondAddress={bondAddress}
        amount={amount}
        assetId={assetId}
        price={lastPrice}
        faceAsset={faceAsset}
      />
    </Box>
  );
};

export default CommitPhase;
